/**
 * Standard Config (Screen 3) — v0M3.7.
 *
 * Six slot cards (faction name + Player/AI/Neutral role pills) + form
 * controls (Difficulty, Victory Condition, Starting Mode, Map, Seed) +
 * Start button. Emits a validated Scenario via `emitScenario`; on failure
 * the caller shows the error inline via `onError`.
 */

import { getFactionByName } from '../../data/factions';
import type { Scenario } from '../../sim/scenario';
import type { MenuState, StandardConfigDraft, SlotDraft } from './menu-state';
import { emitScenario, setSlotRole } from './menu-state';

export interface StandardConfigCallbacks {
  onStart: (scenario: Scenario) => void;
  onError: (message: string) => void;
  onBack: () => void;
  /** Called whenever any form control mutates the draft — trigger a rerender. */
  onDraftChange: () => void;
}

export function renderStandardConfig(
  panel: HTMLElement,
  state: MenuState,
  cb: StandardConfigCallbacks,
): void {
  const back = document.createElement('button');
  back.className = 'su-menu-back';
  back.textContent = '← Back';
  back.addEventListener('click', cb.onBack);
  panel.appendChild(back);

  const h = document.createElement('h2');
  h.className = 'su-menu-title';
  h.textContent = 'Standard Game';
  panel.appendChild(h);

  if (state.banner !== null) {
    const banner = document.createElement('div');
    banner.className = 'su-menu-banner su-menu-error';
    banner.textContent = state.banner;
    panel.appendChild(banner);
  }

  panel.appendChild(renderSlotList(state.draft, cb.onDraftChange));
  panel.appendChild(
    renderFormRow('Map', renderMapSelect(state.draft, cb.onDraftChange)),
  );
  panel.appendChild(
    renderFormRow('Starting mode', renderStartingModeSelect(state.draft, cb.onDraftChange)),
  );
  panel.appendChild(
    renderFormRow('Victory', renderVictorySelect(state.draft, cb.onDraftChange)),
  );
  panel.appendChild(
    renderFormRow('Difficulty', renderDifficultySelect(state.draft, cb.onDraftChange)),
  );
  panel.appendChild(
    renderFormRow('Map exploration', renderMapExplorationSelect(state.draft, cb.onDraftChange)),
  );
  panel.appendChild(renderFormRow('Seed', renderSeedRow(state.draft, cb.onDraftChange)));

  const startBtn = document.createElement('button');
  startBtn.className = 'su-menu-button su-primary';
  startBtn.textContent = 'Start';
  startBtn.addEventListener('click', () => {
    const result = emitScenario(state.draft);
    if (result.ok) {
      cb.onStart(result.scenario);
    } else {
      cb.onError(result.error);
    }
  });
  panel.appendChild(startBtn);
}

// ---------------------------------------------------------------------------
// Slot list
// ---------------------------------------------------------------------------

function renderSlotList(draft: StandardConfigDraft, onChange: () => void): HTMLElement {
  const list = document.createElement('div');
  list.className = 'su-menu-slot-list';

  for (let i = 0; i < draft.slots.length; i++) {
    const slot = draft.slots[i]!;
    list.appendChild(renderSlotCard(draft, i, slot, onChange));
  }
  return list;
}

function renderSlotCard(
  draft: StandardConfigDraft,
  slotIndex: number,
  slot: SlotDraft,
  onChange: () => void,
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'su-menu-slot-card';

  // Faction name row (with canonical color swatch).
  const name = document.createElement('div');
  name.className = 'su-menu-slot-name';
  const swatch = document.createElement('span');
  swatch.className = 'su-menu-slot-swatch';
  const faction = getFactionByName(slot.factionName);
  const [r, g, b] = faction.color;
  swatch.style.background = `rgb(${r}, ${g}, ${b})`;
  name.appendChild(swatch);
  const label = document.createElement('span');
  label.textContent = slot.factionName;
  name.appendChild(label);
  card.appendChild(name);

  // Role pill row.
  const pills = document.createElement('div');
  pills.className = 'su-menu-role-row';
  pills.appendChild(makeRolePill('Player', slot.role === 'player', () => {
    setSlotRole(draft, slotIndex, 'player');
    onChange();
  }));
  pills.appendChild(makeRolePill('AI', slot.role === 'ai', () => {
    setSlotRole(draft, slotIndex, 'ai');
    onChange();
  }));
  pills.appendChild(makeRolePill('Neutral', slot.role === 'neutral', () => {
    setSlotRole(draft, slotIndex, 'neutral');
    onChange();
  }));
  card.appendChild(pills);

  return card;
}

function makeRolePill(label: string, active: boolean, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = active ? 'su-menu-role-pill su-active' : 'su-menu-role-pill';
  btn.textContent = label;
  btn.addEventListener('click', onClick);
  return btn;
}

// ---------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------

function renderFormRow(label: string, control: HTMLElement): HTMLElement {
  const row = document.createElement('div');
  row.className = 'su-menu-row';
  const l = document.createElement('label');
  l.className = 'su-menu-label';
  l.textContent = label;
  row.appendChild(l);
  row.appendChild(control);
  return row;
}

function renderMapSelect(draft: StandardConfigDraft, onChange: () => void): HTMLSelectElement {
  const sel = document.createElement('select');
  sel.className = 'su-menu-select';
  addOption(sel, 'karth', 'Karth (demo)');
  sel.value = draft.mapName;
  sel.addEventListener('change', () => {
    draft.mapName = sel.value;
    onChange();
  });
  return sel;
}

function renderStartingModeSelect(draft: StandardConfigDraft, onChange: () => void): HTMLSelectElement {
  const sel = document.createElement('select');
  sel.className = 'su-menu-select';
  addOption(sel, 'single-province', 'Single Province — one per faction');
  addOption(sel, 'country-divided', 'Country Divided — random spread');
  addOption(sel, 'country-united', 'Country United — contiguous regions');
  sel.value = draft.startingMode;
  sel.addEventListener('change', () => {
    draft.startingMode = sel.value as typeof draft.startingMode;
    onChange();
  });
  return sel;
}

function renderVictorySelect(draft: StandardConfigDraft, onChange: () => void): HTMLSelectElement {
  const sel = document.createElement('select');
  sel.className = 'su-menu-select';
  addOption(sel, 'destroy-capitals', 'Destroy all enemy capitals');
  addOption(sel, 'destroy-cities', 'Destroy all enemy cities');
  sel.value = draft.victoryCondition;
  sel.addEventListener('change', () => {
    draft.victoryCondition = sel.value as typeof draft.victoryCondition;
    onChange();
  });
  return sel;
}

function renderDifficultySelect(draft: StandardConfigDraft, onChange: () => void): HTMLSelectElement {
  const sel = document.createElement('select');
  sel.className = 'su-menu-select';
  addOption(sel, 'easy', 'Easy');
  addOption(sel, 'normal', 'Normal');
  addOption(sel, 'hard', 'Hard');
  sel.value = draft.difficulty;
  sel.addEventListener('change', () => {
    draft.difficulty = sel.value as typeof draft.difficulty;
    onChange();
  });
  return sel;
}

/**
 * v0M5.2 (2026-07-25) — Map-exploration toggle per `MAP.md` §7.2.
 * Values map to `Scenario.startExplored`:
 *   'unexplored' → false (default) — every cell starts state-1 black.
 *   'explored'   → true            — every cell starts state-2 (terrain visible).
 * Live enemy visibility (state 3/4) is still gated by BFS propagation
 * either way; this toggle only changes the initial "explored" bit.
 */
function renderMapExplorationSelect(draft: StandardConfigDraft, onChange: () => void): HTMLSelectElement {
  const sel = document.createElement('select');
  sel.className = 'su-menu-select';
  addOption(sel, 'unexplored', 'Map Unexplored (discover as you go)');
  addOption(sel, 'explored', 'Map Explored (terrain visible from turn 1)');
  sel.value = draft.startExplored ? 'explored' : 'unexplored';
  sel.addEventListener('change', () => {
    draft.startExplored = sel.value === 'explored';
    onChange();
  });
  return sel;
}

function renderSeedRow(draft: StandardConfigDraft, onChange: () => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'flex:1; display:flex; gap:8px;';

  const input = document.createElement('input');
  input.type = 'number';
  input.className = 'su-menu-number';
  input.value = String(draft.seed);
  input.min = '0';
  input.max = '4294967295';
  input.addEventListener('change', () => {
    const n = Number(input.value);
    if (Number.isFinite(n) && n >= 0) {
      draft.seed = Math.floor(n) >>> 0;
      onChange();
    }
  });
  wrap.appendChild(input);

  const rand = document.createElement('button');
  rand.className = 'su-menu-button';
  rand.textContent = 'Randomise';
  rand.style.cssText = 'margin:0; padding:6px 12px; width:auto; font-size:13px;';
  rand.addEventListener('click', () => {
    draft.seed = Math.floor(Math.random() * 0x100000000) >>> 0;
    onChange();
  });
  wrap.appendChild(rand);

  return wrap;
}

function addOption(sel: HTMLSelectElement, value: string, label: string): void {
  const opt = document.createElement('option');
  opt.value = value;
  opt.textContent = label;
  sel.appendChild(opt);
}
